import React, { useState } from 'react';

import { Icon, Popover, Selectable, FilterButton, ExclusiveUnion, useTectonTheme } from '@tecton';
import {
  EuiSelectableGroupLabelOption,
  EuiSelectableLIOption,
} from '@elastic/eui/src/components/selectable/selectable_option';
import { css } from '@emotion/css';

const filterButtonCustomPadding = css`
  .euiButtonContent {
    padding-left: 12px;
    padding-right: 12px;
  }
`;

interface SelectableOptionInterface {
  columnKey: string;
  label: string;
  checked?: 'on';
}

export type ColumnsOptionType = ExclusiveUnion<
  EuiSelectableGroupLabelOption<SelectableOptionInterface>,
  EuiSelectableLIOption<SelectableOptionInterface>
>;

interface ColumnDisplayControlsProps {
  isColumnControlsOpen: boolean;
  setIsColumnControlsOpen: (isOpen: boolean) => void;
  columnOptions: ColumnsOptionType[];
  numberOfSelectedColumnsOptions: number;
  setColumnOptions: (items: ColumnsOptionType[]) => void;
}

const parseLocalStorageColumnOptions = (
  data: object,
  initialState: ColumnsOptionType[]
): ColumnsOptionType[] | undefined => {
  // Parse object and make sure it conforms
  // to ColumnsOptionType

  if (Array.isArray(data)) {
    const keys: Record<string, 'on' | undefined> = {};

    data.forEach((d: any) => {
      if (
        typeof d === 'object' &&
        d.columnKey &&
        typeof d.columnKey === 'string' &&
        (d.checked === 'on' || d.checked === undefined)
      ) {
        keys[d.columnKey] = d.checked;
      }
    });

    return initialState.map((d): ColumnsOptionType => {
      return {
        label: d.label,
        columnKey: d.columnKey,
        checked: keys[d.columnKey] || undefined,
      };
    });
  } else {
    return undefined;
  }
};

const fetchFromLocalStorage = (key: string, initialState: ColumnsOptionType[]): ColumnsOptionType[] | undefined => {
  const storedValue = window.localStorage.getItem(key);
  return storedValue !== null ? parseLocalStorageColumnOptions(JSON.parse(storedValue), initialState) : initialState;
};

const useColumnsDisplayControlsState = (
  tableKey: string,
  initialState?: ColumnsOptionType[],
  disable?: boolean,
  forcedEnabled: string[] = []
) => {
  const [isColumnControlsOpen, setIsColumnControlsOpen] = useState<boolean>(false);

  const localStorageState = disable ? null : fetchFromLocalStorage(tableKey, initialState || []);

  const [columnOptions, unwrappedSetColumnOptions] = useState<ColumnsOptionType[]>(
    localStorageState || initialState || []
  );

  const setColumnOptions = (options: ColumnsOptionType[]) => {
    window.localStorage.setItem(tableKey, JSON.stringify(options));

    unwrappedSetColumnOptions(options);
  };

  const numberOfSelectedColumnsOptions: number = columnOptions.reduce((memo, current) => {
    return current.checked === 'on' ? memo + 1 : memo;
  }, 0);

  const activeColumns: string[] = [
    ...columnOptions.filter((o) => o.checked === 'on').map((o) => o.columnKey),
    ...forcedEnabled,
  ];

  return {
    isColumnControlsOpen,
    setIsColumnControlsOpen,
    columnOptions,
    setColumnOptions,
    numberOfSelectedColumnsOptions,
    activeColumns,
  };
};

const MAX_DROPDOWN_HEIGHT = 500;

const ColumnDisplayControls = ({
  isColumnControlsOpen,
  setIsColumnControlsOpen,
  columnOptions,
  setColumnOptions,
}: ColumnDisplayControlsProps) => {
  const { theme } = useTectonTheme();

  const rowHeight = 2 * theme.font.scale.l * theme.base;

  return (
    <Popover
      panelPaddingSize="none"
      button={
        <FilterButton
          iconType="arrowDown"
          onClick={() => {
            setIsColumnControlsOpen(!isColumnControlsOpen);
          }}
          isSelected={isColumnControlsOpen}
          className={filterButtonCustomPadding}
        >
          <Icon type="tableDensityNormal" style={{ marginRight: theme.size.s }} />
          Columns
        </FilterButton>
      }
      isOpen={isColumnControlsOpen}
      closePopover={() => {
        setIsColumnControlsOpen(false);
      }}
    >
      <Selectable
        searchable
        searchProps={{
          placeholder: 'Columns list',
          compressed: true,
        }}
        aria-label="Feature Views"
        options={columnOptions}
        listProps={{
          paddingSize: 's',
          rowHeight,
        }}
        height={Math.min(rowHeight * columnOptions.length, MAX_DROPDOWN_HEIGHT)}
        renderOption={(option) => {
          return <span style={{ fontSize: theme.font.scale.m * theme.base + 'px' }}>{option.label}</span>;
        }}
        onChange={(newOptions) => {
          setColumnOptions(newOptions);
        }}
        loadingMessage="Loading Columns"
        emptyMessage="No Columns available"
        noMatchesMessage="No columns found"
      >
        {(list) => <div style={{ width: 300 }}>{list}</div>}
      </Selectable>
    </Popover>
  );
};

export default ColumnDisplayControls;
export { useColumnsDisplayControlsState };
